import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { ToolbarService } from '../../core/services/toolbar.service';
import { Liability } from '../models/liability.model';
import { UrgentLiabilities } from '../models/model';
import { SearchLiabilities } from '../models/search-liabilities.model';

@Injectable({
  providedIn: 'root',
})
export class UrgentLiabilitiesService {
  public data: Observable<UrgentLiabilities>;
  public refresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public tagIds$ = new BehaviorSubject<string[]>([]);
  public dataLoading$ = new BehaviorSubject<boolean>(false);

  private frameCopy: Liability;
  private urgentLiabilitiesCopy: BehaviorSubject<UrgentLiabilities> = new BehaviorSubject(null);

  constructor(private api: ApiService, private toolbarService: ToolbarService) {
    const dataFetch = this.refresh.pipe(
      tap(() => this.dataLoading$.next(true)),
      switchMap(() => this.tagIds$),
      switchMap(() => this.toolbarService.tagIds$),
      switchMap((tagIds) =>
        this.api.get<UrgentLiabilities>(`/liabilities/urgent`, { tagId: tagIds.concat(this.tagIds$.value) }),
      ),
    );

    this.data = combineLatest([
      dataFetch.pipe(
        tap((data) => this.urgentLiabilitiesCopy.next(data)),
        switchMap(() => this.urgentLiabilitiesCopy),
      ),
      this.api.subscribe<Liability>('liability').pipe(startWith(null)),
    ]).pipe(
      map(([data, socketFrame]: [SearchLiabilities, Liability]) => {
        if (socketFrame && socketFrame !== this.frameCopy) {
          this.frameCopy = socketFrame;
          let modifiedData = { ...data };

          if (socketFrame.status === 'DELETED') {
            modifiedData = {
              ...data,
              liabilities: data.liabilities.filter((liability) => liability.id !== socketFrame.id),
            };
          } else {
            const index = data.liabilities.findIndex((liability) => liability.id === socketFrame.id);

            if (index >= 0) {
              modifiedData = {
                ...data,
                liabilities: data.liabilities.map((item) => (item.id === socketFrame.id ? socketFrame : item)),
              };
            } else {
              modifiedData.liabilities.push(socketFrame);
            }
          }

          this.urgentLiabilitiesCopy.next(modifiedData);

          return modifiedData;
        }

        return data;
      }),
      tap(() => this.dataLoading$.next(false)),
    );
  }
}
